import { DEFAULT_MEASUREMENT, showOnlineExclusiveFeatureFlag } from "@utils/default";
import { currencyFormatter } from "@utils/formatedPrice";
import { contentfulImageLoader } from "@utils/imageLoader";
import { transparentPixel } from "@utils/string";
import { useSession } from "next-auth/react";
import { useFormatter, useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import Link from "next/link";
import { useContext, useEffect, useMemo, useState } from "react";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import CardTags from "@/components/AutogeneratedCarousel/CardTags";
import InnerCardCarousel from "@/components/AutogeneratedCarousel/InnerCardCarousel";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import CurrencyIcon from "@/components/ui/CurrencyIcon/CurrencyIcon";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { UserContext } from "@/context/UserContext";
import { ecomProductCardClick, propertyCardClickTracking } from "@/dataLayer/dataLayer";
import { useProjectUrl } from "@/hooks/useProjectUrl";
import { UnitTypeCardListProps } from "@/types";
import cn from "@/utils/cn";
import { getQuarter } from "@/utils/dateUtils";

const studioPossibleValue = ["studio", "Studio", "STUDIO", "STD", "std"];

export default function UnitTypesCardList({
  item,
  customClass,
  serverSideCookieCurrency,
  currencyFromApiRes,
  isSearchResultPage = false,
  communitiesData,
  hasUniformUnitPrice,
}: UnitTypeCardListProps) {
  const t = useTranslations();
  const { currency, measurement } = useContext(UserContext);
  const format = useFormatter();
  const locale = useLocale();
  const [language, _] = locale.split("-");
  const { data: session } = useSession();
  const [isLoadingForPrice, setIsLoadingForPrice] = useState(true);
  const formattedPrice = useMemo(() => {
    try {
      const formatted = currencyFormatter(item?.price || 0, currency || "AED", format);
      return formatted;
    } catch (e) {
      return "";
    }
  }, [item.price, currency, format]);

  const handleClick = () => {
    if (typeof window !== "undefined" && item?.goldenVisa && typeof item.slug === "string") {
      const propertyTypeCard = { [item.slug]: item.goldenVisa || true };
      window.sessionStorage.setItem("propertitypeCard", JSON.stringify(propertyTypeCard));
    }
  };

  const deliveryDate = getQuarter(item.availabilityDate!, locale, t);
  // const tags = item.goldenVisa ? [{ label: "Golden Visa", isHiglighted: true }, ...(item.tags || [])] : item.tags;

  const tags = useMemo(() => {
    let combinedTags = item.tags || [];

    // Add "Golden Visa" if applicable
    if (item.goldenVisa) {
      combinedTags = [{ label: t("filter.label.others.goldenVisaText"), isHiglighted: true }, ...combinedTags];
    }

    // Add "Online Exclusive" if applicable
    if (item.exclusive && showOnlineExclusiveFeatureFlag) {
      combinedTags = [{ label: t("filter.label.onlineExclusive"), isHiglighted: false }, ...combinedTags];
    }

    return combinedTags;
  }, [item.tags, item.goldenVisa, item.exclusive]);

  const cardTitle = useMemo(() => {
    //TODO uniform this as function
    if (item.bedrooms.value) {
      if (studioPossibleValue.includes(item.bedrooms.value.toString().toLowerCase())) {
        return (
          t("label.propertyType.studio") +
          " " +
          // (locale?.split("-")?.[0] === "ar" ? t("label.common.of") : "") +
          t(`label.propertyType.type.${item.unitType ?? ""}`)
        );
      } else if (locale?.split("-")?.[0] === "zh") {
        return t("label.propertyType.brName", { count: `${item.bedrooms.value}` }) + t(`label.propertyType.type.${item.unitType ?? ""}`);
      } else if (locale?.split("-")?.[0] === "de") {
        return (
          t(`label.propertyType.type.${item.unitType ?? ""}`) +
          " " +
          "" +
          t("label.common.of") +
          " " +
          t("label.propertyType.brName", { bedrooms: `${item.bedrooms.value}`, count: `${item.bedrooms.value}` })
        );
      } else if (locale?.split("-")?.[0] === "fr") {
        return t(`label.propertyType.type.${item.unitType ?? ""}`) + " " + t("label.propertyType.brName", { count: `${item.bedrooms.value}` });
      } else if (locale?.split("-")?.[0] === "ru") {
        return item?.unitType === "Apartment" || item?.unitType === "apartment" || item?.unitType === "APARTMENT"
          ? t("label.propertyType.ruName", { count: `${item.bedrooms.value}` })
          : t(`label.propertyType.type.${item.unitType ?? ""}`) +
              " " +
              t("label.propertyType.brName", { bedrooms: `${item.bedrooms.value}`, count: `${item.bedrooms.value}` });
      } else {
        return locale?.split("-")?.[0] === "ar"
          ? t(`label.propertyType.type.${item.unitType ?? ""}`) +
              " " +
              t("label.propertyType.brName", { bedrooms: `${item.bedrooms.value}`, count: `${item.bedrooms.value}` })
          : t("label.propertyType.brName", { bedrooms: `${item.bedrooms.value}` }) + " " + t(`label.propertyType.type.${item.unitType ?? ""}`);
      }
    } else {
      return t("label.propertyType.studio");
    }
  }, [item, language, t]);

  useEffect(() => {
    if (serverSideCookieCurrency && currencyFromApiRes && currency) {
      if (serverSideCookieCurrency === currencyFromApiRes && currencyFromApiRes === currency) {
        setIsLoadingForPrice(false);
      } else {
        setIsLoadingForPrice(true);
      }
    }
  }, [serverSideCookieCurrency, currencyFromApiRes, currency]);

  // Communities matching and creating a URL - Start
  const finalUrl = useProjectUrl({ communitiesData: communitiesData || [], item, locale, isPropertyType: true });
  // console.log("finalUrlfinalUrl", item);
  // Communities matching and creating a URL - End
  return (
    <article
      className={cn(
        `group flex flex-col rounded-lg stroke-black p-0 text-black transition-shadow duration-500 md:p-xs md:hover:bg-[#fff9] md:hover:shadow-extra-strong`,
        customClass,
      )}
    >
      <div className="relative">
        {item?.images?.length > 1 ? (
          <InnerCardCarousel item={item} />
        ) : (
          <Image
            className="aspect-4-3 shadow-strong rounded-lg w-full"
            alt={item?.images?.[0]?.title || item?.title || ""}
            src={item?.images?.[0]?.url || ""}
            width={345}
            height={258}
            quality={65}
          />
        )}

        {tags && tags.length > 0 && <CardTags tags={tags} className="z-20 w-[60%] md:absolute md:w-[80%]" />}

        <AddToFavorites item={item} className="absolute right-xs top-xs z-10 hidden md:block" setTransparent={true} />

        <div className="mt-m flex flex-col gap-s text-s font-secondary-light">
          <h3 className="flex items-center text-m font-medium">
            <span>
              {cardTitle}
              {item?.status && <span className="font-secondary-light before:ml-xxs before:mr-xs before:content-['\\2022']">{item.status}</span>}
            </span>
            <AddToFavorites item={item} className="relative z-20 ml-auto block md:hidden" fillColor="fill-black" />
          </h3>
          <p className="min-h-[29px]">{item?.location && t(item.location)}</p>
          <p className="font-normal">
            {hasUniformUnitPrice ? t("label.common.from") + " " : t("label.common.from") + " "}
            {!isSearchResultPage && isLoadingForPrice ? (
              t("common.loading")
            ) : locale?.split("-")?.[0] === "ar" && formattedPrice.includes("د.إ.") ? (
              <>
                {/* {formattedPrice.replace("AED", "").trim()} */}
                {formattedPrice.replace(/AED|د\.إ\./g, "").trim()}
                <CurrencyIcon color="black" />
              </>
            ) : formattedPrice.includes("AED") ? (
              <>
                {/* {formattedPrice.replace("AED", "").trim()} */}
                <CurrencyIcon color="black" />
                <span className="ml-3">{formattedPrice.replace(/AED|د\.إ\./g, "").trim()}</span>
              </>
            ) : (
              formattedPrice
            )}
          </p>
          <div className="grid grid-cols-2 gap-xs font-secondary-light">
            <div className="flex items-center gap-xxs">
              <Icon name="bedroom" size="16" title="bed" />
              {(item.bedrooms.value as string).trim().toLowerCase() !== "studio"
                ? Number(item?.bedrooms?.value) === 1
                  ? `${item?.bedrooms?.value} ${t("label.common.bedroom")}`
                  : t(`${item?.bedrooms?.value} ${item?.bedrooms?.label}`)
                : t(item.bedrooms?.label)}
            </div>
            <p className="flex items-center gap-xxs">
              <Icon name="size" size="16" title="turnkey" />
              {t(`label.project.upTo`)}
              {` ${Math.round(Number(item.sqFt?.value))} ${t(`label.project.${measurement === DEFAULT_MEASUREMENT ? "sq.ft" : "sq.mt"}`)}`}
            </p>
            {/*<p className="flex items-center gap-xxs">
               <Icon name="furnished" size="16" title="furniture" />
              {item.furniture} 
            </p>*/}
            {!deliveryDate.includes("9999") && (
              <p data-availability={item.availabilityDate} className="flex items-center gap-xxs">
                <Icon name="turnkey" size="16" title="available" />
                {deliveryDate !== "ready" ? <span dir="ltr">{deliveryDate}</span> : t("label.ready")}
              </p>
            )}
          </div>
          <div className="invisible hidden items-center font-medium md:block md:group-hover:visible">
            <Button
              href={item.cta.link.url}
              // href={finalUrl}
              size={ButtonSize.SMALL}
              theme={ButtonTheme.BLACK}
              type="button"
              variant={ButtonVariant.PRIMARY}
              customClass="flex justify-center w-full"
            >
              {t(item.cta.label === "explore" ? "label.explore" : item.cta.label)}
            </Button>
          </div>
        </div>
        <Link
          href={finalUrl}
          // href={`${item.cta.link.url}${filteredResults?.length === 1 ? `/${filteredResults[0]?.name?.replaceAll("/", "-")}/` : ""}`}
          className="absolute left-0 top-0 h-full w-full"
          data-title={cardTitle}
          onClick={() => {
            handleClick();
            // const projectTitle = item.title?.replaceAll(" ", "-").toLowerCase();
            // const projectName = item.project?.replaceAll(" ", "-").toLowerCase();
            const productIdentifier = `${item.projectSlug ?? ""}/${item.slug ?? ""}`;

            propertyCardClickTracking(item.project || "");
            ecomProductCardClick({
              product_identifier: productIdentifier,
              card_name: item.title,
              property_name: item.project || item.location?.split("-")?.[0] || item.projectSlug?.replaceAll("-", " ") || "",
              project_location: item.location || "",
              project_type: item.unitType || "",
              bedroom_numbers: (item.bedrooms.value ? (item.bedrooms.value as string) : "") || ("" as string),
              unit_type: item.furniture || "",
              unit_size: `${Math.round(Number(item.sqFt?.value))} ${t(`label.project.${measurement === DEFAULT_MEASUREMENT ? "sq.ft" : "sq.mt"}`)}`,
              unit_price: Math.round(Number(item.price) / 100),
              currency: currency,
              posession_time: getQuarter(item.availabilityDate!, locale, t),
              user_logged_in: !!session,
              // user_id: session?.user.id || "",
            });
          }}
        >
          <span className="sr-only">{item.cta.label}</span>
        </Link>
      </div>
    </article>
  );
}
